/**
 * usePrewarmPersonalizedQuestions.ts - Generate the follow-up chips BEFORE the welcome screen asks
 *
 * The personalized chips cost a model call (the server budgets 10s for it), and they used to be
 * requested at the worst possible moment: when the user opens a new chat and is staring at an
 * empty screen waiting to be shown something.
 *
 * The server caches the generated set per (user, circle) keyed on the id of the user's newest
 * message, so the wait is avoidable entirely — the key is already final the moment an answer
 * lands. Calling the same endpoint then makes the next welcome screen a pure cache hit on both
 * sides: the server row is written, and the SWR entry the panel reads is primed here directly.
 *
 * Two deliberate choices:
 *
 * - DEBOUNCED, not immediate. A generation is charged per call and capped per user per day, so
 *   firing on every answer in a fast back-and-forth would burn the budget generating sets that
 *   the next message immediately invalidates. Waiting out a pause coalesces a burst into one
 *   call, and because the server derives the key from the newest message itself, the one call
 *   that does run is automatically the one keyed to the latest question.
 * - FAILURE IS SILENT. This is an optimisation; nothing renders from it. A failure costs the
 *   user nothing but the old wait, so it must never surface an error or reject into the void.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useSWRConfig } from 'swr';
import { useApi } from 'core';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import {
    fetchPersonalizedQuestions,
    personalizedQuestionsKey,
    personalizedQuestionsLocale,
} from './usePersonalizedQuestions';

/**
 * Long enough to swallow a normal follow-up (the user reads the answer, then types), short
 * enough that the set is ready before they could plausibly open a new chat.
 */
export const PREWARM_DELAY_MS = 3000;

export interface PrewarmControls {
    /** Queue a pre-warm, replacing any already queued. Safe to call on every answer. */
    schedule: () => void;
    /** Drop a queued pre-warm — the conversation continued, so its key is already stale. */
    cancel: () => void;
}

export const usePrewarmPersonalizedQuestions = (circleId: string | null): PrewarmControls => {
    const api = useApi();
    const { mutate } = useSWRConfig();
    const { profile, isAuthenticated } = useAuthContext();
    const { i18n } = useTranslation();

    const userId = isAuthenticated ? profile?._id : null;
    const locale = personalizedQuestionsLocale(i18n.language);

    // The timer outlives the render that scheduled it, so the run reads its inputs from a ref
    // rather than closing over them — otherwise a locale or login change between scheduling and
    // firing would warm a key the panel will not read.
    const inputs = useRef({ api, mutate, userId, circleId, locale });
    inputs.current = { api, mutate, userId, circleId, locale };

    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancel = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    const schedule = useCallback(() => {
        cancel();
        timer.current = setTimeout(() => {
            timer.current = null;
            const current = inputs.current;
            const key = personalizedQuestionsKey(current.userId, current.circleId, current.locale);
            if (!key) return;
            // Written with revalidate:false because this response IS the fresh value — letting SWR
            // revalidate afterwards would spend a second model call to learn what we just fetched.
            current.mutate(
                key,
                fetchPersonalizedQuestions(current.api, current.circleId as string, current.locale),
                { revalidate: false },
            ).catch(() => {
                // An optimisation that failed. The welcome screen falls back to asking for itself.
            });
        }, PREWARM_DELAY_MS);
    }, [cancel]);

    // A pre-warm that fires after the widget is gone can only write a cache nobody will read.
    useEffect(() => cancel, [cancel]);

    return { schedule, cancel };
};
